/**
 * SourcePicker — "People lists" step of Combine & Filter. A thin wrapper over
 * the shared `ImportPicker` in its MULTI-SELECT mode (sub-task 04 addition):
 * `selectedIds` / `onToggle` instead of `selectedId` / `onSelect`, capped at
 * `MAX_COMBINE_SOURCES` (1-20 lists — a SINGLE list is a legitimate way to
 * just FILTER it, 2026-09-23; raised from 10 on 2026-10-08). The cap is never
 * shown: no row is disabled for it and there is no "Max N" pill — a toggle
 * past it is refused and `onMaxReached` fires (the page's warning toast),
 * and the `combined` chip
 * included — a combined list can itself be a source for another combine.
 *
 * REVERTED to this shape (2026-09-23, orchestrator correction to the UI-parity
 * addendum): a prior pass replaced this with a bespoke search-box-opens-a-
 * dropdown control, to match the prototype's own layout more closely. The
 * product correction: use the SAME list search + filter component every
 * other picker screen uses (`ImportSelectionBar` / the AI Review page /
 * BCT) — same search box, "Imported by" owner menu, type chips, table and
 * pagination — rather than a one-off. `ImportPicker`'s own row highlighting
 * (`isSelected ? "border-border bg-accent-muted ring-1 ring-inset ring-accent" : ...`,
 * a checked checkbox) IS the "selected" affordance; a second, separate "selected
 * lists" row list underneath would duplicate that state confusingly, so
 * this does not render one — the card header's tally (`CombineFilterPage`)
 * is the only additional summary.
 *
 * A list that is not `ready` yet renders disabled with its reason
 * (`combineUnusable`, bug hunt 2026-09-23 F12) — the preview would 409 on it.
 *
 * No local state: `selected` is owned by `CombineFilterPage` (it feeds the
 * recipe every other step reads, plus the tally and the preview table's
 * "Lists" column lookups), same "controlled from above" shape the rest of
 * the wizard follows. This component derives the `Set<string>` `ImportPicker`
 * wants from it.
 */
import { useMemo } from "react";

import type { ImportSummary, ImportsClient } from "../../../../clients/imports.client";
import { CompareStateChip } from "../../../../features/imports/selection/CompareStateChip";
import { ImportPicker } from "../../../../features/imports/selection/ImportPicker";
import { combineUnusable } from "../../../../features/imports/selection/requirements";

/**
 * 1-20 lists (epic README, "Sources" row; raised from 10 on 2026-10-08).
 * PINNED to the API's `_MAX_COMBINE_SOURCES` (`packages/api/app/web/v2/
 * imports/schemas.py`) — same pinning as `requestBudget.ts`'s byte budget:
 * change one, change both, or the picker allows a pick the API answers with
 * 422 (or refuses one the API would take).
 */
export const MAX_COMBINE_SOURCES = 20;

export interface SourcePickerProps {
  client: ImportsClient;
  meEmail: string;
  /** Selected lists — `CombineFilterPage` also reads this for the card
   *  header's tally and the preview table's "Lists" column, so it hands
   *  over the full rows, not just ids. */
  selected: readonly ImportSummary[];
  onToggle: (item: ImportSummary) => void;
  /** Called when the recruiter tries to add a list past
   *  `MAX_COMBINE_SOURCES` — the toggle is refused (see `ImportPicker`). */
  onMaxReached?: () => void;
  /** Lists with a Compare the page started or follows still in flight: their
   *  chip reads "Comparing…" like the page's Compare card, not the list's
   *  last run (PR #962 follow-up F8). */
  comparingIds?: ReadonlySet<string>;
}

export function SourcePicker({
  client,
  meEmail,
  selected,
  onToggle,
  onMaxReached,
  comparingIds,
}: SourcePickerProps) {
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.import_id)), [selected]);
  return (
    <div data-testid="combine-source-picker" className="flex flex-col gap-2">
      <ImportPicker
        client={client}
        meEmail={meEmail}
        selectedIds={selectedIds}
        onToggle={onToggle}
        maxSelected={MAX_COMBINE_SOURCES}
        onMaxReached={onMaxReached}
        includeCombinedChip
        defaultOwner="all"
        unusable={combineUnusable}
        renderRowAdornment={(item) => (
          <CompareStateChip
            compare={item.compare}
            comparing={comparingIds?.has(item.import_id) ?? false}
          />
        )}
      />
    </div>
  );
}
